import React, { useState } from "react";
import { Button } from "./ui/button";
import { CheckCircle, ArrowRight, Phone, Mail, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import ContactModal from "./ContactModal";
import NoteGoogle from "./NoteGoogle";
import AnnuaireFranceRenov from "./AnnuaireFranceRenov";
import LienAidesComplementaires from "./LienAidesComplementaires";

const Hero = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <section className="relative py-20 overflow-hidden min-h-screen">
      {/* Hero Image RESPONSIVE avec fetchpriority HIGH */}
      <picture>
        <source media="(max-width: 768px)" srcSet="/images/hero-home-mobile.webp" />
        <source media="(min-width: 769px)" srcSet="/images/hero-home.webp" />
        <img
          src="/images/hero-home.webp"
          width="1920"
          height="1280"
          alt="Adaptation logement MaPrimeAdapt'"
          fetchpriority="high"
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </picture>
      
      {/* Overlay pour améliorer la lisibilité */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/70 to-white/60 z-0"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                <CheckCircle className="h-4 w-4 mr-2" />
                AMO agréé ANAH • Île-de-France
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Adaptez votre logement avec{" "}
                <span className="text-orange-700 block mt-2">MaPrimeAdapt'</span>
              </h1>

              <p className="text-xl text-gray-700 leading-relaxed">
                Nous vous accompagnons dans le montage de votre dossier MaPrimeAdapt' au vieillissement et au handicap.
                Nos artisans partenaires réalisent vos travaux, à hauteur de 22 000€ maximum. Bénéficiez jusqu'à <strong className="text-orange-700">15 400€ d'aides</strong> pour vos travaux d'accessibilité.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-orange-700" />
                </div>
                <span className="text-gray-700">Accompagnement AMO obligatoire (600€ - 800€ si ergothérapeute) pris en charge</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-orange-700" />
                </div>
                <span className="text-gray-700">Diagnostic autonomie liberté inclus</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-orange-700" />
                </div>
                <span className="text-gray-700">Artisans labellisés et de confiance</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/simulateur">
                <Button className="bg-orange-700 hover:bg-orange-800 text-white shadow-lg group w-full sm:w-auto">
                  Tester mon éligibilité
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="border-2 border-blue-200 hover:bg-blue-50 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" aria-hidden="true" />
                Nous contacter
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-orange-200 hover:bg-orange-50 w-full sm:w-auto"
                aria-label="Appeler le 09 39 03 54 44" onClick={() => window.location.href = 'tel:0939035444'}
              >
                <Phone className="mr-2 h-5 w-5" aria-hidden="true" />
                09 39 03 54 44
              </Button>
            </div>

            <NoteGoogle className="pt-2" />
            <AnnuaireFranceRenov />

            <div className="flex items-center space-x-6 pt-6 border-t border-gray-100">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">0 €</div>
                <div className="text-sm text-gray-700">Coût de l'AMO pour vous</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">289</div>
                <div className="text-sm text-gray-700">Dossiers réalisés</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">2-6 mois</div>
                <div className="text-sm text-gray-700">Délai moyen</div>
              </div>
            </div>
          </div>

          {/* Card example financier */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Exemple de financement</h2>
                  <p className="text-sm text-gray-700">Adaptation salle de bain pour Mme Dupont, 73 ans</p>
                </div>

                {/* Exemple arithmétiquement vérifiable : l'aide vaut 70 % des
                    travaux retenus, dans la limite de 22 000 € de travaux —
                    soit 15 400 € au maximum, et non pour n'importe quel devis. */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-700">Son devis de travaux</span>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">18 500 €</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700">Aide de l'ANAH (MaPrimeAdapt', 70 %)</span>
                      <span className="text-green-700 font-semibold whitespace-nowrap">- 12 950 €</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t-2 border-orange-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Reste à charge</span>
                      <span className="text-2xl font-bold text-gray-400 whitespace-nowrap line-through decoration-2 decoration-orange-400">5 550 €</span>
                    </div>
                    <LienAidesComplementaires />
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-800">
                    Ménage aux ressources très modestes. L'accompagnement AMO est
                    financé par l'ANAH : il ne vous coûte rien.{" "}
                    <Link to="/simulateur" className="underline hover:text-orange-900">
                      Estimer mon reste à charge
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Badges décoratifs : réduits et ramenés dans l'écran sur mobile */}
            <div className="absolute -top-3 -right-1 p-2 sm:-top-6 sm:-right-6 sm:p-4 bg-orange-700 text-white rounded-lg shadow-lg transform rotate-3">
              <div className="text-center">
                <Mail className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1" aria-hidden="true" />
                <div className="text-xs font-medium">Devis gratuit</div>
              </div>
            </div>

            <div className="absolute -bottom-3 -left-1 p-2 sm:-bottom-6 sm:-left-6 sm:p-4 bg-green-700 text-white rounded-lg shadow-lg transform -rotate-3">
              <div className="text-center">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1" aria-hidden="true" />
                <div className="text-xs font-medium">AMO certifié</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />
    </section>
  );
};

export default Hero;
